/**
 * The Targeting / Budget / Conversions tabs of the Meta campaign detail page (#5981).
 *
 * Google-parity tabs with Meta-native semantics — each exists because Meta has a real concept
 * behind it, not because Google has the tab:
 *
 * - Targeting: Google's sub-tab layout (Locations | Languages | Excluded Locations) over the
 *   campaign's persisted selection — Google's exact table/section chrome (pill, reach, per-row
 *   edit/delete, language list) saved through the partial targeting PATCH.
 * - Budget: Google's Budget & Bidding layout — Daily Budget with the Manage Budget modal and a
 *   Bidding Strategy panel over Meta's real strategies (Lowest Cost / Cost Cap) plus the local
 *   Target CPL detector threshold.
 * - Conversions: Google's linked-conversions layout over Meta's pipeline — HubSpot stages
 *   uploading via CAPI (link/unlink), plus the bound Pixel and upload health.
 *
 * Deliberately omitted (no Meta analog): Negatives, search-ad extension sub-tabs, tracking template.
 */

import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { AlertCircle, Languages, Lightbulb, Pencil, Plus, Settings2, Sparkles, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { ExcludedLocationsSection } from '@/components/campaign/settings';
import {
  LocationTargetsTable,
  locationTargetRowLabel,
  toProximityInput,
  useBusinessLocationCandidates,
  useLocationTargetSort,
  type LocationTargetRow,
} from '@/components/campaign/settings/location-targets-table';
import { BudgetV2 } from '@/components/business/budget-v2';
import { useMetaAllocatedDailyWhole } from '@/components/business/meta-budget-tab';
import { CountryRegionPicker } from '@/components/business/business-locations/country-region-picker';
import { LocationMapPicker } from '@/components/business/business-locations/location-map-picker';
import type { RadiusFormValue } from '@/components/business/business-locations/radius-form';
import {
  businessQueryOptions,
  locationsByIdsQueryOptions,
  metaConversionEventsQueryOptions,
  metaConversionsQueryOptions,
  useUpdateMetaConversionSettingsMutation,
  metaCustomAudiencesQueryOptions,
  metaInterestSearchQueryOptions,
  metaLocaleSearchQueryOptions,
  metaIntegrationStatusQueryOptions,
  useLinkMetaConversionMutation,
  useUnlinkMetaConversionMutation,
  metaBudgetRecommendationQueryOptions,
  useUpdateMetaAdSetBudgetMutation,
  useUpdateMetaCampaignBudgetModeMutation,
  useUpdateMetaCampaignTargetingMutation,
} from '@/lib/query-options';
import { useToast } from '@/hooks/useToast';
import { metaAudienceDraftAtom, metaBudgetDraftAtom, metaInterestsDraftAtom, metaLocationsDraftAtom, metaPlacementsDraftAtom, organizationDataAtom, type MetaBudgetDraft } from '@/store';
import { derivePlacementsChecked } from '@/components/campaign/meta/meta-detail-adapter';
import { META_OBJECTIVE_RESULT_NOUN, META_PLACEMENT_CATALOG } from '@/lib/types';
import type {
  CampaignProximityInput,
  ExcludedLocation,
  Location,
  MetaBidStrategy,
  MetaAttributionEntry,
  MetaCampaignDetailResponse,
  MetaCampaignOutcome,
  MetaConversionEventOption,
  MetaInterestEntry,
  MetaLocaleEntry,
} from '@/lib/types';

/** One row of the unified conversions table — HubSpot (removable) or Pixel (informational). */
type ConversionsTableRow = {
  key: string;
  name: string;
  event_name: string;
  source: string;
  count: number;
  stage_mapping_id?: number;
};
import type { ColumnDef } from '@tanstack/react-table';

// --- Targeting ------------------------------------------------------------------------------------

// Persisted proximity shape → the picker's form shape. Meta-only: coordinate-less radii map to
// null and are preserved separately by the save paths (#6163 P0-2). The row model, sort, and
// table are shared with Google in location-targets-table.tsx (#6182).
const toRadiusFormValue = (r: CampaignProximityInput): RadiusFormValue | null =>
  r.latitude != null && r.longitude != null
    ? {
        lat: r.latitude,
        lng: r.longitude,
        radiusKm: r.radius_km,
        countryCode: null,
        formattedAddress: r.address ?? `${r.latitude}, ${r.longitude}`,
      }
    : null;

/**
 * Locations: the persisted catalog selection (names/reach from the shared Location cache) in
 * Google's exact table, edited through the same LocationMapPicker dialog. The picker edits the
 * campaign-keyed draft live and Done just closes; the toolbar's Save Changes commits every staged
 * slice in one targeting PATCH — exactly like Google's CampaignLocationsSection + page save bar
 * (#6178). Row deletes stage the remaining selection the same way.
 */
function MetaTargetingLocations({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  const { toast } = useToast();
  const organizationData = useAtomValue(organizationDataAtom);
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });
  const businessId = business?.id;
  const isDraft = campaign.meta_campaign_id == null;

  // The picker edits this campaign-keyed draft live; the toolbar's Save Changes commits it in place of
  // Go Live — exactly like Google's CampaignLocationsSection + page save bar (#6178). Geo is held as
  // resolved Location rows so the table/picker render without re-resolving criteria ids.
  const [locationsDraft, setLocationsDraft] = useAtom(metaLocationsDraftAtom);
  const isStaged = locationsDraft?.campaignId === campaign.id;

  const savedCriteriaIds = useMemo(
    () => campaign.target_location_criteria_ids ?? [],
    [campaign.target_location_criteria_ids],
  );
  const { data: savedGeoLocations } = useQuery(locationsByIdsQueryOptions(savedCriteriaIds));
  // Ready once the saved geo has resolved (or there's none / a draft is active) — until then, editing
  // would stage an empty geo list and wipe the saved targets (#6163 P0-1).
  const geoReady = isStaged || savedCriteriaIds.length === 0 || savedGeoLocations != null;

  const geoLocations: Location[] = isStaged ? locationsDraft!.locations : (savedGeoLocations ?? []);
  const radiusInputs: CampaignProximityInput[] = isStaged ? locationsDraft!.radii : (campaign.target_radii ?? []);

  const stage = (locations: Location[], radii: CampaignProximityInput[]) =>
    setLocationsDraft({ campaignId: campaign.id, locations, radii });
  const stageLocations = (locations: Location[]) => stage(locations, radiusInputs);
  const stageRadii = (formRadii: RadiusFormValue[]) => {
    // Preserve persisted coordinate-less radii (the picker can't represent them) so a radius edit
    // doesn't drop them (#6163 P0-2).
    const coordinateLess = radiusInputs.filter((r) => r.latitude == null || r.longitude == null);
    stage(geoLocations, [...coordinateLess, ...formRadii.map(toProximityInput)]);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialRadiusEdit, setInitialRadiusEdit] = useState<RadiusFormValue | null>(null);
  const [deletingRow, setDeletingRow] = useState<LocationTargetRow | null>(null);

  // The picker's checklist candidates — shared with Google's CampaignLocationsSection (#6182).
  const { businessCandidates, radiusCandidates } = useBusinessLocationCandidates(businessId, dialogOpen);

  const rows = useMemo<LocationTargetRow[]>(
    () => [
      ...(geoLocations ?? []).map((l) => ({ kind: 'geo' as const, key: `geo-${l.criteria_id}`, location: l })),
      ...radiusInputs.map((r, i) => ({
        kind: 'radius' as const,
        key: `radius-${i}`,
        index: i,
        address: r.address,
        radiusKm: r.radius_km,
        lat: r.latitude,
        lng: r.longitude,
      })),
    ],
    [geoLocations, radiusInputs],
  );

  const { sortedRows, sort, cycleSort } = useLocationTargetSort(rows);

  // Geo-wipe guard (#6163 P0-1, #6179): the geo catalog loads async, and any path that stages
  // while the campaign HAS geo criteria but the catalog is unresolved would stage an empty geo
  // list and wipe the saved targets. The picker stages live once the dialog is open, so the gate
  // sits at every entry: dialog open (add/edit) and row delete.
  const guardGeoCatalogLoaded = (): boolean => {
    if (!geoReady) {
      toast({
        title: 'Still Loading',
        description: 'Target locations are still loading. Try again in a moment.',
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  // Delete just re-stages the draft (the toolbar's Save Changes commits, or a delete-to-empty is
  // rejected there by the slice's validation).
  const confirmDelete = () => {
    if (!deletingRow) return;
    if (!guardGeoCatalogLoaded()) {
      setDeletingRow(null);
      return;
    }
    if (deletingRow.kind === 'geo') {
      stageLocations(geoLocations.filter((l) => l.criteria_id !== deletingRow.location.criteria_id));
    } else {
      stage(
        geoLocations,
        radiusInputs.filter((_, i) => i !== deletingRow.index),
      );
    }
    setDeletingRow(null);
  };

  const openAdd = () => {
    if (!guardGeoCatalogLoaded()) return;
    setInitialRadiusEdit(null);
    setDialogOpen(true);
  };

  const openEdit = (row: LocationTargetRow) => {
    if (!guardGeoCatalogLoaded()) return;
    // Radius rows jump the picker to that radius for editing; geo rows just open the picker
    // (geo targets are catalog selections, edited by re-picking) — same as Google's.
    if (row.kind === 'radius' && row.lat != null && row.lng != null) {
      setInitialRadiusEdit({
        lat: row.lat,
        lng: row.lng,
        radiusKm: row.radiusKm,
        countryCode: null,
        formattedAddress: row.address ?? `${row.lat}, ${row.lng}`,
      });
    } else {
      setInitialRadiusEdit(null);
    }
    setDialogOpen(true);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Locations</h3>
          {/* Copy matches Google's Targeting → Locations section verbatim (#5981 — no text drift). */}
          <p className="mt-1 text-sm text-gray-700">Target geographic areas and radius zones for your ads.</p>
        </div>
        {/* Disable reason is a visible note, not a title: the Button's disabled:pointer-events-none
            means tooltips can never fire on a disabled control (#6163 P1-7, #6179). */}
        <div className="flex flex-col items-end gap-1">
          <Button variant="outline" className="text-gray-900" onClick={openAdd} disabled={isDraft}>
            <Plus className="mr-2 h-4 w-4" />
            Add Locations
          </Button>
          {isDraft && (
            <p className="text-[11px] leading-snug text-amber-700">
              Available once the campaign has finished generating.
            </p>
          )}
        </div>
      </div>

      <LocationTargetsTable
        sortedRows={sortedRows}
        sort={sort}
        onCycleSort={cycleSort}
        showActions={!isDraft}
        onEdit={openEdit}
        onDelete={setDeletingRow}
      />

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setInitialRadiusEdit(null);
        }}
      >
        <DialogContent
          className="flex h-[85vh] max-h-[85vh] w-[92vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1100px] [&>button]:top-4"
          aria-describedby={undefined}
        >
          <DialogHeader className="border-b border-gray-200 px-5 py-4">
            <DialogTitle className="truncate">
              {initialRadiusEdit ? 'Edit radius' : 'Add locations'}
              {campaign.name ? ` for ${campaign.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-hidden">
            {businessId != null ? (
              <LocationMapPicker
                businessId={businessId}
                selected={geoLocations}
                onChange={stageLocations}
                candidates={businessCandidates ?? []}
                radiusCandidates={radiusCandidates}
                radii={radiusInputs.map(toRadiusFormValue).filter((r): r is RadiusFormValue => r !== null)}
                onRadiiChange={stageRadii}
                initialRadiusEdit={initialRadiusEdit}
              />
            ) : (
              <div className="h-full overflow-y-auto p-5">
                <CountryRegionPicker selected={geoLocations} onChange={stageLocations} />
              </div>
            )}
          </div>
          {/* Done just closes — the picker already staged into the draft; the toolbar's Save Changes
              commits it, exactly like Google's CampaignLocationsSection + page save bar (#6178). */}
          <div className="flex justify-end border-t border-gray-200 px-5 py-3">
            <Button className="bg-[#290BD9] text-white hover:bg-[#2309C5]" onClick={() => setDialogOpen(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={deletingRow !== null}
        onClose={() => setDeletingRow(null)}
        onConfirm={confirmDelete}
        title="Remove Target"
        description={`Remove "${deletingRow ? locationTargetRowLabel(deletingRow) : ''}" from targeting?`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={false}
      />
    </div>
  );
}

/**
 * Audience: the campaign's demographic narrowing (#6076) — age range, gender, and the account's
 * saved custom audiences. View mode shows honest summary rows (Meta's defaults spelled out when
 * nothing is narrowed); Edit saves the whole slice once via the targeting PATCH
 * (replace-whole-object, matching the backend semantics). Age/gender fields can't save
 * per-interaction like the Languages list, hence the Locations-style view/edit flow.
 */
function MetaTargetingAudience({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  const isDraft = campaign.meta_campaign_id == null;
  const saved = campaign.target_audience ?? null;

  // The Edit Audience modal stages the whole slice into a campaign-keyed draft on Done; the toolbar's
  // Save Changes commits it in place of Go Live (#6076). The summary shows the staged edit until saved.
  const [audienceDraft, setAudienceDraft] = useAtom(metaAudienceDraftAtom);
  const current = audienceDraft?.campaignId === campaign.id ? audienceDraft.audience : saved;

  const [isEditing, setIsEditing] = useState(false);
  const [ageMinText, setAgeMinText] = useState('');
  const [ageMaxText, setAgeMaxText] = useState('');
  const [gender, setGender] = useState<'all' | 'men' | 'women'>('all');
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<Set<string>>(new Set());
  const [rangeError, setRangeError] = useState<string | null>(null);

  // The catalog only loads while editing — the view state renders from the current selection.
  const { data: catalog = [], isLoading: isLoadingCatalog } = useQuery({
    ...metaCustomAudiencesQueryOptions(),
    enabled: isEditing,
  });

  const openEdit = () => {
    setAgeMinText(current?.age_min != null ? String(current.age_min) : '');
    setAgeMaxText(current?.age_max != null ? String(current.age_max) : '');
    setGender(current?.genders?.length === 1 ? (current.genders[0] === 1 ? 'men' : 'women') : 'all');
    setSelectedAudienceIds(new Set((current?.custom_audiences ?? []).map((ref) => ref.id)));
    setRangeError(null);
    setIsEditing(true);
  };

  const applyEdit = () => {
    const ageMin = ageMinText.trim() === '' ? null : Number(ageMinText);
    const ageMax = ageMaxText.trim() === '' ? null : Number(ageMaxText);
    for (const value of [ageMin, ageMax]) {
      if (value !== null && (!Number.isInteger(value) || value < 13 || value > 65)) {
        setRangeError('Ages must be whole numbers between 13 and 65.');
        return;
      }
    }
    if (ageMin !== null && ageMax !== null && ageMin > ageMax) {
      setRangeError('Minimum age cannot exceed maximum age.');
      return;
    }
    // Selected ids → {id, name} refs from the catalog, falling back to the current names for
    // audiences the catalog no longer returns (deleted on Meta but still on the campaign).
    const nameById = new Map<string, string>();
    for (const ref of current?.custom_audiences ?? []) nameById.set(ref.id, ref.name);
    for (const entry of catalog) nameById.set(entry.id, entry.name);
    const customAudiences = [...selectedAudienceIds].map((id) => ({ id, name: nameById.get(id) ?? id }));
    setAudienceDraft({
      campaignId: campaign.id,
      audience: {
        age_min: ageMin,
        age_max: ageMax,
        genders: gender === 'all' ? null : [gender === 'men' ? 1 : 2],
        custom_audiences: customAudiences.length > 0 ? customAudiences : null,
      },
    });
    setIsEditing(false);
  };

  // Honest summary rows — Meta's defaults spelled out when nothing is narrowed.
  const ageLabel =
    current?.age_min != null || current?.age_max != null
      ? `${current?.age_min ?? 18} - ${current?.age_max != null ? current.age_max : '65+'}`
      : "18 – 65+ (Meta's default)";
  const genderLabel =
    current?.genders?.length === 1 ? (current.genders[0] === 1 ? 'Men' : 'Women') : 'All genders';
  const audienceNames = (current?.custom_audiences ?? []).map((ref) => ref.name);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Audience</h3>
          <p className="mt-1 text-sm text-gray-700">Narrow who sees your ads by age, gender and saved audiences.</p>
        </div>
        {/* Visible disable reason: a title on a disabled Button never shows (#6163 P1-7, #6179). */}
        <div className="flex flex-col items-end gap-1">
          <Button variant="outline" className="h-9 text-gray-900" onClick={openEdit} disabled={isDraft}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit Audience
          </Button>
          {isDraft && (
            <p className="text-[11px] leading-snug text-amber-700">
              Available once the campaign has finished generating.
            </p>
          )}
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
          <span className="w-32 flex-shrink-0 text-xs font-medium uppercase tracking-wider text-gray-500">Age</span>
          <span className="text-sm text-gray-900">{ageLabel}</span>
        </div>
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
          <span className="w-32 flex-shrink-0 text-xs font-medium uppercase tracking-wider text-gray-500">Gender</span>
          <span className="text-sm text-gray-900">{genderLabel}</span>
        </div>
        <div className="flex items-start gap-3 px-4 py-3">
          <span className="w-32 flex-shrink-0 text-xs font-medium uppercase tracking-wider text-gray-500">
            Saved Audiences
          </span>
          <span className="text-sm text-gray-900">
            {audienceNames.length > 0 ? audienceNames.join(', ') : 'None'}
          </span>
        </div>
      </div>

      {/* Multi-field edits open in a modal rather than swapping the section inline (#6173). */}
      <Dialog open={isEditing} onOpenChange={(open) => { if (!open) setIsEditing(false); }}>
        <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Edit Audience</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700">
            Narrow who sees your ads. Saving replaces the campaign&apos;s current audience; an active
            campaign re-enters Meta&apos;s learning phase.
          </p>
          <div className="space-y-5 overflow-y-auto py-2">
            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-gray-500">Age Range</Label>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  min={13}
                  max={65}
                  value={ageMinText}
                  onChange={(event) => {
                    setAgeMinText(event.target.value);
                    setRangeError(null);
                  }}
                  placeholder="18"
                  className="h-9 w-24"
                />
                <span className="text-sm text-gray-500">to</span>
                <Input
                  type="number"
                  min={13}
                  max={65}
                  value={ageMaxText}
                  onChange={(event) => {
                    setAgeMaxText(event.target.value);
                    setRangeError(null);
                  }}
                  placeholder="65+"
                  className="h-9 w-24"
                />
              </div>
              {rangeError && <p className="mt-1 text-sm text-red-500">{rangeError}</p>}
              <p className="mt-1 text-xs text-gray-500">Leave blank for Meta&apos;s default (18 to 65+).</p>
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-gray-500">Gender</Label>
              <RadioGroup
                value={gender}
                onValueChange={(value) => setGender(value as 'all' | 'men' | 'women')}
                className="mt-2 flex items-center gap-6"
              >
                {(['all', 'men', 'women'] as const).map((option) => (
                  <div key={option} className="flex items-center gap-2">
                    <RadioGroupItem value={option} id={`audience-gender-${option}`} />
                    <Label htmlFor={`audience-gender-${option}`} className="cursor-pointer font-normal text-gray-900">
                      {option === 'all' ? 'All genders' : option === 'men' ? 'Men' : 'Women'}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-gray-500">Saved Audiences</Label>
              {isLoadingCatalog ? (
                <p className="mt-2 py-1 text-sm text-gray-500">Loading…</p>
              ) : catalog.length === 0 && selectedAudienceIds.size === 0 ? (
                <p className="mt-2 py-1 text-sm text-gray-500">
                  No saved audiences on this ad account. Create them in Meta Ads Manager.
                </p>
              ) : (
                <div className="mt-2 max-h-56 space-y-2 overflow-y-auto">
                  {catalog.map((entry) => (
                    <label key={entry.id} className="flex cursor-pointer items-center gap-3 rounded-md border p-3">
                      <Checkbox
                        checked={selectedAudienceIds.has(entry.id)}
                        onCheckedChange={(checked) =>
                          setSelectedAudienceIds((prev) => {
                            const next = new Set(prev);
                            if (checked === true) next.add(entry.id);
                            else next.delete(entry.id);
                            return next;
                          })
                        }
                      />
                      <span className="text-sm text-gray-900">{entry.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="h-9 text-gray-500" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            {/* Done stages the slice; the toolbar's Save Changes commits it in place of Go Live (#6076). */}
            <Button className="h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white" onClick={applyEdit}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Shared live-catalog search for the targeting add dialogs (#6182): one 300ms debounce + Meta
 * server search + the three-state empty copy, replacing the twin copies inside the Languages and
 * Interests dialogs (#6163 P3). Search state lives here — Radix unmounts dialog content on close,
 * so it resets for free. Selection SEMANTICS stay with the caller (languages save per click;
 * interests stage a working set): this renders the search surface only.
 */
function MetaCatalogSearchCommand<T>({
  noun,
  searchQueryOptions,
  selectedKeys,
  getKey,
  getLabel,
  onSelect,
  itemsDisabled = false,
  commandClassName = 'rounded-md border overflow-hidden',
  listClassName = 'max-h-[280px] overflow-y-auto',
}: {
  /** Lowercase plural driving the copy: 'Search {noun}...', 'Type to search {noun}.', 'No {noun} found.' */
  noun: string;
  searchQueryOptions: (query: string) => UseQueryOptions<T[], Error, T[], string[]>;
  /** Keys already picked — filtered out of the result list. */
  selectedKeys: Set<string>;
  getKey: (option: T) => string;
  getLabel: (option: T) => string;
  onSelect: (option: T) => void;
  itemsDisabled?: boolean;
  commandClassName?: string;
  listClassName?: string;
}) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: searchResults, isFetching } = useQuery(searchQueryOptions(debouncedSearch));
  const availableOptions = (searchResults ?? []).filter((option) => !selectedKeys.has(getKey(option)));

  return (
    // Meta's catalogs are searched live (no full-list endpoint), so filtering is server-side —
    // shouldFilter off, the input drives the query.
    <Command shouldFilter={false} className={commandClassName}>
      <CommandInput placeholder={`Search ${noun}...`} autoFocus value={search} onValueChange={setSearch} />
      <CommandList className={listClassName}>
        <CommandEmpty>
          {debouncedSearch.trim().length === 0
            ? `Type to search ${noun}.`
            : isFetching
              ? 'Searching...'
              : `No ${noun} found.`}
        </CommandEmpty>
        <CommandGroup>
          {availableOptions.map((option) => (
            <CommandItem
              key={getKey(option)}
              value={`${getLabel(option)} ${getKey(option)}`}
              disabled={itemsDisabled}
              onSelect={() => onSelect(option)}
              className='cursor-pointer'
            >
              {getLabel(option)}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

/**
 * Languages: Google's Language section cloned over Meta's locale targeting (#5981). Rows come from
 * the campaign's persisted {key, name} pairs; the Add modal searches Meta's own adlocale catalog
 * live (Meta serves the option list, so it can never drift from what Meta accepts — the same
 * guarantee Google's backend-served language catalog gives). Selecting adds immediately; no rows
 * means Meta targets all languages.
 */
function MetaTargetingLanguages({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  const { toast } = useToast();
  const updateTargeting = useUpdateMetaCampaignTargetingMutation();
  const isDraft = campaign.meta_campaign_id == null;

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [localeToRemove, setLocaleToRemove] = useState<MetaLocaleEntry | null>(null);

  const locales = useMemo(() => campaign.target_locales ?? [], [campaign.target_locales]);

  const saveLocales = (next: MetaLocaleEntry[], onSettledClose?: () => void) => {
    updateTargeting.mutate(
      { slug: campaign.slug, body: { locales: next } },
      {
        onSuccess: () => onSettledClose?.(),
        onError: (error) =>
          toast({
            title: 'Could Not Update Languages',
            description: error instanceof Error ? error.message : 'Please try again.',
            variant: 'destructive',
          }),
      },
    );
  };

  const columns = useMemo(
    (): ColumnDef<MetaLocaleEntry>[] => [
      {
        accessorKey: 'name',
        header: 'Language',
        cell: ({ row }) => <span className='text-sm text-gray-900'>{row.original.name}</span>,
      },
      {
        id: 'actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex justify-end'>
            <IconButton variant='destructive' onClick={() => setLocaleToRemove(row.original)} disabled={isDraft}>
              <Trash2 className='h-4 w-4' />
            </IconButton>
          </div>
        ),
        meta: { className: 'px-6 text-right' },
      },
    ],
    [isDraft],
  );

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <div>
          {/* Copy matches Google's Targeting → Languages section verbatim (#5981 — no text drift). */}
          <h3 className='text-lg font-semibold text-gray-900'>Language</h3>
          <p className='text-sm text-gray-700 mt-1'>Target users who browse in specific languages.</p>
        </div>
        {!isDraft && (
          <Button variant='outline' className='text-gray-900' onClick={() => setIsAddOpen(true)}>
            <Plus className='h-4 w-4 mr-2' />
            Add Language
          </Button>
        )}
      </div>

      <DataTable
        data={locales}
        columns={columns}
        variant='bordered'
        pageSize={999}
        getRowId={(row) => String(row.key)}
        emptyIcon={Languages}
        emptyTitle='Targeting All Languages'
        emptyDescription='No language targeting is set, so ads can show in any language. Add a language to narrow targeting.'
      />

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className='sm:max-w-[500px] max-h-[85vh] flex flex-col' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Language</DialogTitle>
          </DialogHeader>
          <div className='py-2 flex-1 min-h-0 flex flex-col'>
            <MetaCatalogSearchCommand
              noun='languages'
              searchQueryOptions={metaLocaleSearchQueryOptions}
              selectedKeys={new Set(locales.map((l) => String(l.key)))}
              getKey={(opt) => String(opt.key)}
              getLabel={(opt) => opt.name}
              onSelect={(opt) => saveLocales([...locales, opt])}
              itemsDisabled={updateTargeting.isPending}
              commandClassName='rounded-md border flex-1 min-h-0 overflow-hidden'
              listClassName='max-h-none flex-1 min-h-0 overflow-y-auto'
            />
            <p className='text-xs text-gray-500 mt-2'>Selecting a language adds it immediately.</p>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsAddOpen(false)} className='leading-none'>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={!!localeToRemove}
        onClose={() => setLocaleToRemove(null)}
        onConfirm={() =>
          localeToRemove &&
          saveLocales(
            locales.filter((l) => l.key !== localeToRemove.key),
            () => setLocaleToRemove(null),
          )
        }
        title='Remove Language'
        description={`Remove "${localeToRemove?.name}" from language targeting?`}
        confirmText='Remove'
        cancelText='Cancel'
        isLoading={updateTargeting.isPending}
      />
    </div>
  );
}

/**
 * Interests: Meta ``flexible_spec`` interest narrowing (#6076) — a clone of the Languages sub-tab.
 * Rows come from the campaign's persisted {id, name} pairs; the Add modal searches Meta's own
 * adinterest catalog live. No rows = broad targeting, which is Meta's default and often its
 * recommendation — the empty state says so instead of nagging.
 */
function MetaTargetingInterests({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  const isDraft = campaign.meta_campaign_id == null;

  const [isAddOpen, setIsAddOpen] = useState(false);
  // The Add Interest modal edits its own working set, applied to the staged draft on 'Add' (and
  // discarded on Cancel).
  const [modalInterests, setModalInterests] = useState<MetaInterestEntry[]>([]);
  const savedInterests = useMemo(() => campaign.target_interests ?? [], [campaign.target_interests]);
  // Staged interest edits (table deletes + modal adds) live in a campaign-keyed atom so the campaign
  // toolbar's Save Changes (shown in place of Go Live) commits them in ONE Meta write, instead of one
  // write per click — which strained Meta's rate limit and stalled around 9 (#6076 followup).
  const [interestsDraft, setInterestsDraft] = useAtom(metaInterestsDraftAtom);
  const stagedInterests = interestsDraft?.campaignId === campaign.id ? interestsDraft.interests : savedInterests;

  const openAdd = () => {
    setModalInterests(stagedInterests);
    setIsAddOpen(true);
  };

  const columns = useMemo(
    (): ColumnDef<MetaInterestEntry>[] => [
      {
        accessorKey: 'name',
        header: 'Interest',
        cell: ({ row }) => <span className='text-sm text-gray-900'>{row.original.name}</span>,
      },
      {
        id: 'actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex justify-end'>
            {/* Stages the removal — the toolbar's Save Changes commits all staged deletes at once. */}
            <IconButton
              variant='destructive'
              aria-label={`Remove ${row.original.name}`}
              onClick={() =>
                setInterestsDraft({
                  campaignId: campaign.id,
                  interests: stagedInterests.filter((i) => i.id !== row.original.id),
                })
              }
              disabled={isDraft}
            >
              <Trash2 className='h-4 w-4' />
            </IconButton>
          </div>
        ),
        meta: { className: 'px-6 text-right' },
      },
    ],
    [isDraft, stagedInterests, campaign.id, setInterestsDraft],
  );

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>Interests</h3>
          <p className='text-sm text-gray-700 mt-1'>Narrow delivery to people Meta classifies by interest.</p>
        </div>
        {!isDraft && (
          <Button variant='outline' className='text-gray-900' onClick={openAdd}>
            <Plus className='h-4 w-4 mr-2' />
            Add Interest
          </Button>
        )}
      </div>

      <DataTable
        data={stagedInterests}
        columns={columns}
        variant='bordered'
        pageSize={999}
        getRowId={(row) => row.id}
        emptyIcon={Sparkles}
        emptyTitle='Targeting Broadly'
        emptyDescription="No interest narrowing is set. Meta finds the right people automatically, which is its recommended default. Add interests to narrow delivery."
      />

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className='sm:max-w-[500px] max-h-[85vh] flex flex-col' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Interest</DialogTitle>
          </DialogHeader>
          <div className='py-2 flex flex-col'>
            {modalInterests.length > 0 && (
              <div className='mb-3 flex flex-wrap gap-1.5'>
                {modalInterests.map((entry) => (
                  <span
                    key={entry.id}
                    className='inline-flex items-center gap-1 rounded-full bg-[#EDE9FE] px-2.5 py-0.5 text-xs font-medium text-[#290BD9]'
                  >
                    {entry.name}
                    <button
                      type='button'
                      aria-label={`Remove ${entry.name}`}
                      onClick={() => setModalInterests((prev) => prev.filter((i) => i.id !== entry.id))}
                      className='ml-0.5 leading-none hover:text-[#2309C5]'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <MetaCatalogSearchCommand
              noun='interests'
              searchQueryOptions={metaInterestSearchQueryOptions}
              selectedKeys={new Set(modalInterests.map((entry) => entry.id))}
              getKey={(option) => option.id}
              getLabel={(option) => option.name}
              onSelect={(option) => setModalInterests((prev) => [...prev, option])}
            />
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsAddOpen(false)} className='leading-none'>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setInterestsDraft({ campaignId: campaign.id, interests: modalInterests });
                setIsAddOpen(false);
              }}
              className='bg-[#290BD9] hover:bg-[#2309C5] text-white'
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

/**
 * Excluded Locations: Google's ExcludedLocationsSection reused as-is over the persisted exclusion
 * selection — same table (pill, reach, per-row edit/delete), same Add Exclusion modal. Google
 * stages edits for the page save bar; Meta PATCHes each change immediately.
 */
function MetaTargetingExclusions({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  const { toast } = useToast();
  const organizationData = useAtomValue(organizationDataAtom);
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });
  const updateTargeting = useUpdateMetaCampaignTargetingMutation();
  const isDraft = campaign.meta_campaign_id == null;

  const excludedIds = useMemo(
    () => campaign.excluded_location_criteria_ids ?? [],
    [campaign.excluded_location_criteria_ids],
  );
  const { data: excludedCatalog } = useQuery(locationsByIdsQueryOptions(excludedIds));
  const excludedLocations = useMemo<ExcludedLocation[]>(
    () =>
      (excludedCatalog ?? []).map((l) => ({
        criteria_id: l.criteria_id,
        name: l.name,
        canonical_name: l.canonical_name,
        country_code: l.country_code,
        target_type: l.target_type,
      })),
    [excludedCatalog],
  );

  const handleChange = (next: ExcludedLocation[]) => {
    updateTargeting.mutate(
      { slug: campaign.slug, body: { excluded_location_criteria_ids: next.map((e) => e.criteria_id) } },
      {
        onError: (error) =>
          toast({
            title: 'Could Not Update Exclusions',
            description: error instanceof Error ? error.message : 'Please try again.',
            variant: 'destructive',
          }),
      },
    );
  };

  return (
    <ExcludedLocationsSection
      excludedLocations={excludedLocations}
      onChange={handleChange}
      businessId={business?.id}
      disabled={isDraft}
    />
  );
}

/**
 * Placements: where ads serve (#6076) — platforms + per-platform positions. The default state is
 * the star: Advantage+ automatic placements is Meta's recommendation, so the view presents it as a
 * first-class choice, not an unconfigured blank. At save time an all-platforms-no-narrowing
 * selection sends empty publisher_platforms — clearing back to Advantage+ rather than persisting
 * an exhaustive list that would pin the campaign to today's surfaces.
 */
function MetaTargetingPlacements({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  const isDraft = campaign.meta_campaign_id == null;
  const saved = campaign.target_placements ?? null;

  // Interactive tree — clicking a checkbox stages the whole selection into a campaign-keyed atom;
  // the toolbar's Save Changes converts it to a placement selection and commits (#6076). Every
  // surface checked = Advantage+ automatic (sent as the empty clear, never an exhaustive list).
  const [placementsDraft, setPlacementsDraft] = useAtom(metaPlacementsDraftAtom);
  const { checkedPlatforms, checkedPositions } = useMemo(() => {
    if (placementsDraft?.campaignId === campaign.id) {
      const positions: Record<string, Set<string>> = {};
      for (const [platform, values] of Object.entries(placementsDraft.checkedPositions)) {
        positions[platform] = new Set(values);
      }
      return { checkedPlatforms: new Set(placementsDraft.checkedPlatforms), checkedPositions: positions };
    }
    return derivePlacementsChecked(saved);
  }, [placementsDraft, campaign.id, saved]);

  const isAutomatic = META_PLACEMENT_CATALOG.every(
    (entry) =>
      checkedPlatforms.has(entry.platform) &&
      entry.positions.every((pos) => checkedPositions[entry.platform]?.has(pos.value)),
  );

  const stage = (platforms: Set<string>, positions: Record<string, Set<string>>) => {
    setPlacementsDraft({
      campaignId: campaign.id,
      checkedPlatforms: [...platforms],
      checkedPositions: Object.fromEntries(Object.entries(positions).map(([key, value]) => [key, [...value]])),
    });
  };

  const togglePlatform = (platform: string, checked: boolean) => {
    const nextPlatforms = new Set(checkedPlatforms);
    const catalogEntry = META_PLACEMENT_CATALOG.find((entry) => entry.platform === platform);
    // The platform box is a select-all: checking it turns every position on, unchecking it turns them
    // all off. The positions stay visible either way — they never collapse (#6175 followup).
    if (checked) nextPlatforms.add(platform);
    else nextPlatforms.delete(platform);
    stage(nextPlatforms, {
      ...checkedPositions,
      [platform]: checked ? new Set((catalogEntry?.positions ?? []).map((pos) => pos.value)) : new Set<string>(),
    });
  };

  const togglePosition = (platform: string, position: string, checked: boolean) => {
    const next = new Set(checkedPositions[platform] ?? []);
    if (checked) next.add(position);
    else next.delete(position);
    // A platform is on as long as one of its positions is: unchecking the last position turns the
    // platform off, and checking a position under an off platform turns it back on.
    const nextPlatforms = new Set(checkedPlatforms);
    if (next.size > 0) nextPlatforms.add(platform);
    else nextPlatforms.delete(platform);
    stage(nextPlatforms, { ...checkedPositions, [platform]: next });
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Placements</h3>
        <p className="mt-1 text-sm text-gray-700">
          {isAutomatic
            ? "Meta automatically delivers your ads across all these surfaces where they're likely to perform best."
            : 'Choose which platforms and surfaces your ads serve on.'}
        </p>
      </div>
      <div className="space-y-3">
        {META_PLACEMENT_CATALOG.map((entry) => {
          const platformChecked = checkedPlatforms.has(entry.platform);
          return (
            <div key={entry.platform} className="rounded-lg border p-3">
              <label className={`flex items-center gap-3 ${isDraft ? '' : 'cursor-pointer'}`}>
                <Checkbox
                  checked={platformChecked}
                  disabled={isDraft}
                  onCheckedChange={(checked) => togglePlatform(entry.platform, checked === true)}
                />
                <span className="text-sm font-medium text-gray-900">{entry.label}</span>
              </label>
              {entry.positions.length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-2 pl-7">
                  {entry.positions.map((position) => (
                    <label
                      key={position.value}
                      className={`flex items-center gap-2 ${isDraft ? '' : 'cursor-pointer'}`}
                    >
                      <Checkbox
                        checked={(checkedPositions[entry.platform] ?? new Set()).has(position.value)}
                        disabled={isDraft}
                        onCheckedChange={(checked) =>
                          togglePosition(entry.platform, position.value, checked === true)
                        }
                      />
                      <span className="text-sm text-gray-700">{position.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isDraft && (
        <p className="mt-3 text-sm italic text-gray-500">
          Placements can be adjusted once the campaign has finished generating.
        </p>
      )}
    </div>
  );
}

/**
 * Targeting — Google's sub-tab layout with Meta's honest capability set (#5981): Locations,
 * Languages, Excluded Locations. Google's remaining two sub-tabs are deliberately absent, not
 * pending:
 *
 * - Presence: Meta removed granular location types ("living in" / "recently in" / "traveling in")
 *   from its API — every campaign now reaches people living in or recently in the targeted area,
 *   so a Presence control would render a choice Meta no longer accepts.
 * - IP Exclusions: Meta's Marketing API has no IP-based exclusion concept at all (Google's is a
 *   Search-network feature); there is nothing to configure on a Meta campaign.
 */
export function MetaTargetingTab({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  type TargetingSubTab = 'locations' | 'audience' | 'interests' | 'languages' | 'excluded' | 'placements';
  const [subTab, setSubTab] = useState<TargetingSubTab>('locations');
  const subTabs: { id: TargetingSubTab; label: string }[] = [
    { id: 'locations', label: 'Locations' },
    { id: 'audience', label: 'Audience' },
    { id: 'interests', label: 'Interests' },
    { id: 'languages', label: 'Languages' },
    { id: 'excluded', label: 'Excluded Locations' },
    { id: 'placements', label: 'Placements' },
  ];

  return (
    <div className="space-y-6">
      {/* Underlined sub-tabs — the exact chrome Google's Targeting tab uses (#5981, no visual drift). */}
      <div className="-mt-2.5 border-b border-gray-200">
        <div className="flex gap-0">
          {subTabs.map((tab) => {
            const isActive = subTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSubTab(tab.id)}
                className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                  isActive
                    ? 'border-[#290BD9] text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {subTab === 'locations' && <MetaTargetingLocations campaign={campaign} />}
      {subTab === 'audience' && <MetaTargetingAudience campaign={campaign} />}
      {subTab === 'interests' && <MetaTargetingInterests campaign={campaign} />}
      {subTab === 'languages' && <MetaTargetingLanguages campaign={campaign} />}
      {subTab === 'excluded' && <MetaTargetingExclusions campaign={campaign} />}
      {subTab === 'placements' && <MetaTargetingPlacements campaign={campaign} />}
    </div>
  );
}

// --- Budget ---------------------------------------------------------------------------------------

// Meta's guidance: budget changes of ~20% or more can retrigger the campaign's learning phase, so
// edits at or past this fraction of the saved budget show the learning warning.
const LEARNING_PHASE_BUDGET_CHANGE_THRESHOLD = 0.2;

/** One editable budget row — used only for the rare multi-ad-set ABO campaign (one input per ad set). */
function BudgetRow({
  label,
  currencySymbol,
  currentCents,
  onSave,
  isSaving,
}: {
  label: string;
  currencySymbol: string;
  currentCents: number | null | undefined;
  onSave: (cents: number) => void;
  isSaving: boolean;
}) {
  // Whole currency units, matching the CBO input, the business Budget tab, and Google's
  // convention — one precision across every budget surface (#6163 P2, #6179).
  const [value, setValue] = useState(currentCents != null ? String(Math.round(currentCents / 100)) : '');
  const parsed = Number(value);
  const isDirty = value.trim() !== '' && Math.round(parsed) * 100 !== (currentCents ?? 0);

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">{currencySymbol}</span>
        <Input
          type="number"
          min="0"
          step="1"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          className="h-9 w-28 text-right"
        />
        <span className="text-sm text-gray-500">/day</span>
        {isDirty && (
          <Button
            variant="outline"
            className="h-9 text-gray-900"
            disabled={isSaving || !(parsed >= 0)}
            onClick={() => onSave(Math.round(parsed) * 100)}
          >
            {isSaving && <Spinner size="sm" className="mr-2" />}
            Save
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Daily Budget — Google's CampaignBudgetView layout (title, currency input, Manage Budget link
 * with the "/day available" figure, over-allocation + learning-phase warnings) over Meta's budget
 * placement: CBO campaigns edit the campaign budget, single-ad-set ABO campaigns edit their ad
 * set's. Meta has no page save bar, so a dirty value shows explicit Save/Cancel buttons. The
 * currency follows the Meta AD ACCOUNT (passed in), not the org default — hence the inline input
 * instead of CampaignBudgetInput (which is hardwired to the org symbol).
 */
function MetaDailyBudgetSection({
  campaign,
  currencySymbol,
}: {
  campaign: MetaCampaignDetailResponse;
  currencySymbol: string;
}) {
  const { toast } = useToast();
  const cs = currencySymbol;
  const organizationData = useAtomValue(organizationDataAtom);
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });
  const adSetBudget = useUpdateMetaAdSetBudgetMutation();
  const queryClient = useQueryClient();
  // Per-row in-flight tracking: `mutation.variables` only reflects the LATEST call, so with two
  // concurrent ABO row saves the first row's spinner dropped early (#6179).
  const [savingAdSetIds, setSavingAdSetIds] = useState<Set<number>>(new Set());
  const metaAllocatedDaily = useMetaAllocatedDailyWhole();

  const isDraft = campaign.meta_campaign_id == null;
  const soleAdSet = !campaign.is_cbo && campaign.ad_sets.length === 1 ? campaign.ad_sets[0] : null;
  const isMultiAdSet = !campaign.is_cbo && campaign.ad_sets.length > 1;
  const savedCents = campaign.is_cbo ? (campaign.daily_budget_cents ?? 0) : (soleAdSet?.daily_budget_cents ?? 0);
  const savedWhole = Math.round(savedCents / 100);

  // Edits stage into the campaign-keyed draft atom; the toolbar's Save Changes commits them
  // (Google's draft + toolbar-save flow — #5981, no inline save button here).
  const [budgetDraft, setBudgetDraft] = useAtom(metaBudgetDraftAtom);
  const draftForThis = budgetDraft?.campaignId === campaign.id ? budgetDraft : null;
  const stage = (budgetWhole: number) =>
    setBudgetDraft((prev) => ({
      ...(prev?.campaignId === campaign.id ? prev : {}),
      campaignId: campaign.id,
      budgetWhole,
    }));
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(false);
  const [recommendation, setRecommendation] = useState<number | null>(null);
  const edited = draftForThis?.budgetWhole ?? null;
  const value = edited ?? savedWhole;

  // Remaining Meta platform allocation, computed like Google's remainingDailyBudget: the Meta
  // platform cap minus every OTHER Meta campaign's allocation (i.e. the headroom this campaign
  // may grow into). Null when no Meta platform budget is set or totals are still loading.
  const metaPlatformMonthly = business?.platform_budgets?.find((b) => b.platform === 'Meta')?.amount;
  const remainingDailyBudget =
    metaPlatformMonthly != null && metaPlatformMonthly > 0 && metaAllocatedDaily != null
      ? Math.floor(metaPlatformMonthly / 30) - (metaAllocatedDaily - savedWhole)
      : null;
  const isOverAllocation = remainingDailyBudget != null && value > remainingDailyBudget;
  const showLearningWarning =
    campaign.status === 'ACTIVE' &&
    savedWhole > 0 &&
    value > 0 &&
    Math.abs(value - savedWhole) / savedWhole >= LEARNING_PHASE_BUDGET_CHANGE_THRESHOLD;

  // Google's "Get recommendation" block, Meta-grounded: the backend derives the figure from
  // Meta's learning-phase guidance (actual trailing CPL, else the Target CPL) — see #5981.
  const handleGetRecommendation = async () => {
    setIsLoadingRecommendation(true);
    try {
      // Imperative fetch through query-options (the data-access rule) — fresh on every click (#6182).
      const result = await queryClient.fetchQuery(metaBudgetRecommendationQueryOptions(campaign.slug));
      const recommendedWhole = Math.round(result.daily_budget_cents / 100);
      setRecommendation(recommendedWhole);
      // Stage the figure into the field (the toolbar's Save Changes appears) — asking for a
      // recommendation IS the intent to use it; the user still confirms with Save Changes.
      if (recommendedWhole !== savedWhole) stage(recommendedWhole);
    } catch (error) {
      toast({
        title: 'Could Not Get Recommendation',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingRecommendation(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        {/* Copy matches Google's Budget tab verbatim (#5981 — no text drift). */}
        <h2 className="text-xl font-semibold text-gray-900">Daily Budget</h2>
        <p className="text-sm text-gray-600 mt-1">
          {campaign.status === 'ACTIVE'
            ? 'Your daily budget for this campaign.'
            : 'Set your daily budget. Approval is required before going live.'}
        </p>
      </div>

      {isMultiAdSet ? (
        // Meta-native fallback: an ABO campaign with several ad sets carries one budget per ad
        // set — a single input can't represent it honestly, so each ad set gets its own row.
        <div className="space-y-3">
          {campaign.ad_sets.map((adSet) => (
            <BudgetRow
              key={adSet.id}
              label={adSet.name}
              currencySymbol={cs}
              currentCents={adSet.daily_budget_cents}
              // Only the row being saved spins/disables (#6163 P1-14), tracked in the per-row
              // in-flight set so concurrent saves each keep their own spinner (#6179).
              isSaving={savingAdSetIds.has(adSet.id)}
              onSave={(cents) => {
                setSavingAdSetIds((prev) => new Set(prev).add(adSet.id));
                adSetBudget.mutate(
                  { adSetId: adSet.id, daily_budget_cents: cents },
                  {
                    onError: (error) =>
                      toast({
                        title: 'Could Not Update Budget',
                        description: error instanceof Error ? error.message : 'Please try again.',
                        variant: 'destructive',
                      }),
                    onSettled: () =>
                      setSavingAdSetIds((prev) => {
                        const next = new Set(prev);
                        next.delete(adSet.id);
                        return next;
                      }),
                  },
                );
              }}
            />
          ))}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className="flex w-[120px] items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2">
              <span className="text-sm text-gray-500">{cs}</span>
              <input
                type="number"
                min="0"
                step="1"
                value={value === 0 && edited == null ? '' : value}
                placeholder="0"
                disabled={isDraft}
                onChange={(event) => stage(Math.max(0, Math.round(Number(event.target.value) || 0)))}
                className="w-full border-none bg-transparent p-0 text-right text-sm font-medium text-gray-900 outline-none focus:ring-0 disabled:text-gray-400"
              />
            </div>
            {!isDraft && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setIsBudgetModalOpen(true)}
                    className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-[#290BD9] transition-colors hover:text-[#2309C5]"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    Manage Budget
                  </button>
                  {remainingDailyBudget != null && !isOverAllocation && (
                    <span className="text-sm text-gray-500">
                      ({cs}
                      {(remainingDailyBudget - value).toLocaleString()}/day available)
                    </span>
                  )}
                </div>
                {campaign.objective === 'OUTCOME_LEADS' && (
                <button
                  onClick={handleGetRecommendation}
                  disabled={isLoadingRecommendation}
                  className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-[#290BD9] transition-colors hover:text-[#2309C5] disabled:opacity-50"
                >
                  {isLoadingRecommendation ? (
                    <Spinner size="sm" />
                  ) : (
                    <Lightbulb className="h-3.5 w-3.5" />
                  )}
                  {isLoadingRecommendation
                    ? 'Getting recommendation...'
                    : recommendation !== null
                      ? `Recommended: ${cs}${recommendation}/day`
                      : 'Get recommendation'}
                </button>
                )}
              </div>
            )}
          </div>
          {campaign.spend_cap_cents != null && (
            <p className="mt-2 text-xs text-gray-500">
              Campaign spend cap: {cs}
              {Math.round(campaign.spend_cap_cents / 100).toLocaleString()} (from Account Limits: a
              lifetime backstop, refreshed when the Meta allocation changes)
            </p>
          )}
          {showLearningWarning && (
            <div className="mt-3 flex items-start gap-2 text-sm text-amber-600">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                A change of this size may retrigger the campaign learning phase. Smaller incremental adjustments
                are recommended.
              </span>
            </div>
          )}
          {isOverAllocation && (
            <div className="mt-3 flex items-start gap-2 text-sm text-red-600">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Exceeds platform allocation. Max available: {cs}
                {Math.max(0, remainingDailyBudget!).toLocaleString()}/day. Click{' '}
                <button
                  onClick={() => setIsBudgetModalOpen(true)}
                  className="cursor-pointer font-medium text-red-600 underline hover:text-red-700"
                >
                  Manage Budget
                </button>{' '}
                to adjust your platform budgets.
              </span>
            </div>
          )}
        </>
      )}

      <Dialog open={isBudgetModalOpen} onOpenChange={setIsBudgetModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[1100px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Manage Budget</DialogTitle>
          </DialogHeader>
          <BudgetV2 initialTab="meta" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Bidding Strategy — Google's panel layout over Meta's real strategies (#5981): Lowest Cost
 * (Meta's default, ≈ Google's Maximize Conversions) and Cost Cap (≈ Target CPA; pushes
 * COST_CAP + the cap to Meta). The Target CPL input under Lowest Cost is OUR optimization
 * threshold (the Phase 6 winner/loser detectors), not a Meta field — it saves even on drafts.
 * Minimum ROAS and manual bid caps are deliberately absent: Min ROAS requires purchase-value
 * optimization, which OUTCOME_LEADS campaigns (valueless Lead events) can never satisfy.
 */
function MetaBiddingStrategyPanel({
  campaign,
  currencySymbol,
}: {
  campaign: MetaCampaignDetailResponse;
  currencySymbol: string;
}) {
  const cs = currencySymbol;
  const isDraft = campaign.meta_campaign_id == null;

  const savedStrategy: MetaBidStrategy = campaign.bid_strategy ?? 'LOWEST_COST_WITHOUT_CAP';
  const savedCapText = campaign.bid_amount_cents != null ? String(campaign.bid_amount_cents / 100) : '';
  const savedCplText = campaign.target_cpl_cents != null ? String(campaign.target_cpl_cents / 100) : '';

  // Edits stage into the campaign-keyed draft atom (strategy, cap, Target CPL alike); the
  // toolbar's Save Changes commits them — Google's draft + toolbar-save flow (#5981).
  const [budgetDraft, setBudgetDraft] = useAtom(metaBudgetDraftAtom);
  const draftForThis = budgetDraft?.campaignId === campaign.id ? budgetDraft : null;
  const stage = (patch: Partial<Pick<MetaBudgetDraft, 'bidStrategy' | 'capText' | 'cplText'>>) =>
    setBudgetDraft((prev) => ({
      ...(prev?.campaignId === campaign.id ? prev : {}),
      campaignId: campaign.id,
      ...patch,
    }));
  const strategy = draftForThis?.bidStrategy ?? savedStrategy;
  const capText = draftForThis?.capText ?? savedCapText;
  const cplText = draftForThis?.cplText ?? savedCplText;
  // Copy follows the objective's delivery result (#6076) — a traffic campaign gets "landing page
  // views", not "leads". Target CPL is the lead detectors' threshold, so its input only renders
  // for lead campaigns (the backend gate skips everything else — promising it would be dishonest).
  const resultNoun = META_OBJECTIVE_RESULT_NOUN[campaign.objective] ?? 'results';
  const isLeadObjective = campaign.objective === 'OUTCOME_LEADS';

  return (
    <div>
      {/* Copy matches Google's Bidding Strategy panel verbatim (#5981 — no text drift). */}
      <h2 className="text-xl font-semibold text-gray-900">Bidding Strategy</h2>
      <p className="text-sm text-gray-600 mt-1 mb-4">Choose how to optimize your bidding.</p>
      <RadioGroup
        value={strategy}
        onValueChange={(value) => stage({ bidStrategy: value as MetaBidStrategy })}
        disabled={isDraft}
        className="space-y-4"
      >
        <div className="flex items-start space-x-3">
          <RadioGroupItem value="LOWEST_COST_WITHOUT_CAP" id="lowest-cost" className="mt-1" />
          <div className="flex-1">
            <Label htmlFor="lowest-cost" className="cursor-pointer font-normal">
              <div className="font-semibold text-gray-900">Lowest Cost</div>
              <div className="text-sm text-gray-500 mt-1">Get the most {resultNoun} from your budget.</div>
            </Label>
            {strategy === 'LOWEST_COST_WITHOUT_CAP' && isLeadObjective && (
              <div className="mt-3">
                <div className="flex items-center gap-3">
                  <div className="relative" style={{ minWidth: '100px' }}>
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {cs}
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={cplText}
                      onChange={(event) => stage({ cplText: event.target.value })}
                      placeholder="0.00"
                      className="!ring-inset !ring-transparent pl-7 text-gray-900"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    Target Cost Per Lead (optional). Used to judge winning and losing ads automatically.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-start space-x-3">
          <RadioGroupItem value="COST_CAP" id="cost-cap" className="mt-1" />
          <div className="flex-1">
            <Label htmlFor="cost-cap" className="cursor-pointer font-normal">
              <div className="font-semibold text-gray-900">Cost Cap</div>
              <div className="text-sm text-gray-500 mt-1">
                Keep your average cost per result below a set cap.
              </div>
            </Label>
            {strategy === 'COST_CAP' && (
              <div className="mt-3">
                <div className="flex items-center gap-3">
                  <div className="relative" style={{ minWidth: '100px' }}>
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {cs}
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={capText}
                      onChange={(event) => stage({ capText: event.target.value })}
                      placeholder="0.00"
                      disabled={isDraft}
                      className="!ring-inset !ring-transparent pl-7 text-gray-900"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    The most to pay per result on average. Meta may exceed it slightly while learning.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </RadioGroup>
      {isDraft && (
        <p className="mt-3 text-sm text-gray-500">
          Strategy changes are available once the campaign has finished generating.
        </p>
      )}
    </div>
  );
}

/**
 * Budget — Google's Budget tab layout with Meta-native semantics (#5981): one "Budget & Bidding"
 * sub-tab holding Daily Budget (left) and Bidding Strategy (right). Google's other two sub-tabs
 * are deliberately absent, not pending:
 *
 * - Bid adjustments: device/location/schedule bid modifiers are Google Search bidding concepts —
 *   Meta's delivery system exposes no per-dimension bid modifiers to adjust.
 * - Scheduling: Meta only supports ad scheduling on lifetime-budget campaigns; ours are
 *   daily-budget by design (the cross-platform budget model is daily), so there is nothing to
 *   schedule.
 */
/**
 * Budget Mode switcher (#6211) — ABO ↔ CBO on a live campaign, the Budget tab's home for the
 * field Jeff asked to move out of Overview. Switching reveals the inputs the target mode needs
 * (CBO: one campaign budget; ABO: a budget per active ad set), confirms explicitly, and rides
 * the adset_budgets Meta mechanism. Meta rate-limits the toggle (~90 min) — its rejection message
 * names the wait and surfaces verbatim.
 */
function MetaBudgetModeSection({
  campaign,
  currencySymbol,
}: {
  campaign: MetaCampaignDetailResponse;
  currencySymbol: string;
}) {
  const { toast } = useToast();
  const modeMutation = useUpdateMetaCampaignBudgetModeMutation();
  const [isSwitching, setIsSwitching] = useState(false);
  const activeAdSets = campaign.ad_sets.filter((adSet) => adSet.status !== 'ARCHIVED');
  const isDraft = campaign.meta_campaign_id == null;

  // Prefills: to CBO, the campaign inherits the sum of today's ad-set budgets; to ABO, the
  // campaign budget splits evenly. Whole-currency inputs like every budget field on this tab.
  const summedAdSetWhole = Math.round(
    activeAdSets.reduce((sum, adSet) => sum + (adSet.daily_budget_cents ?? 0), 0) / 100,
  );
  const evenSplitWhole = activeAdSets.length
    ? Math.max(1, Math.floor((campaign.daily_budget_cents ?? 0) / 100 / activeAdSets.length))
    : 0;
  const [campaignBudgetWhole, setCampaignBudgetWhole] = useState(0);
  const [adSetBudgetsWhole, setAdSetBudgetsWhole] = useState<Record<number, number>>({});
  const [markedInvalid, setMarkedInvalid] = useState(false);

  const openSwitch = () => {
    setCampaignBudgetWhole(summedAdSetWhole || Math.round((campaign.daily_budget_cents ?? 0) / 100));
    setAdSetBudgetsWhole(Object.fromEntries(activeAdSets.map((adSet) => [adSet.id, evenSplitWhole])));
    setMarkedInvalid(false);
    setIsSwitching(true);
  };

  const handleConfirm = async () => {
    const toCbo = !campaign.is_cbo;
    if (toCbo ? campaignBudgetWhole <= 0 : activeAdSets.some((adSet) => (adSetBudgetsWhole[adSet.id] ?? 0) <= 0)) {
      setMarkedInvalid(true);
      return;
    }
    try {
      await modeMutation.mutateAsync({
        slug: campaign.slug,
        body: toCbo
          ? { is_cbo: true, campaign_daily_budget_cents: campaignBudgetWhole * 100 }
          : {
              is_cbo: false,
              ad_set_budget_cents: Object.fromEntries(
                activeAdSets.map((adSet) => [adSet.id, (adSetBudgetsWhole[adSet.id] ?? 0) * 100]),
              ),
            },
      });
      setIsSwitching(false);
    } catch (error) {
      // Server rejection — notably Meta's toggle cooldown, whose message names the wait time.
      toast({
        title: 'Could Not Switch Budget Mode',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-gray-900">Budget Mode</h3>
          <p className="mt-1 text-sm text-gray-600">
            {campaign.is_cbo
              ? 'Campaign Budget Optimization (CBO): one campaign budget, distributed by Meta across ad sets.'
              : 'Ad-set level budgets (ABO): each ad set owns its own daily budget.'}
          </p>
        </div>
        {!isSwitching && (
          <Button
            variant="outline"
            className="h-9 shrink-0 text-gray-900"
            onClick={openSwitch}
            disabled={isDraft || activeAdSets.length === 0}
          >
            Switch to {campaign.is_cbo ? 'ABO' : 'CBO'}
          </Button>
        )}
      </div>
      {isSwitching && (
        <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
          {campaign.is_cbo ? (
            <>
              <p className="text-sm text-gray-900">Give each ad set its own daily budget:</p>
              {activeAdSets.map((adSet) => (
                <div key={adSet.id} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm text-gray-900">{adSet.name}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-500">{currencySymbol}</span>
                    <Input
                      type="number"
                      min={1}
                      value={adSetBudgetsWhole[adSet.id] ?? ''}
                      error={markedInvalid && (adSetBudgetsWhole[adSet.id] ?? 0) <= 0}
                      onChange={(e) =>
                        setAdSetBudgetsWhole((prev) => ({ ...prev, [adSet.id]: Number(e.target.value) }))
                      }
                      className="w-28 text-right"
                    />
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-900">Campaign daily budget (ad-set budgets move up and lock):</span>
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-500">{currencySymbol}</span>
                <Input
                  type="number"
                  min={1}
                  value={campaignBudgetWhole || ''}
                  error={markedInvalid && campaignBudgetWhole <= 0}
                  onChange={(e) => setCampaignBudgetWhole(Number(e.target.value))}
                  className="w-28 text-right"
                />
              </div>
            </div>
          )}
          <p className="text-sm text-gray-500">
            Meta allows budget mode to change roughly once every 90 minutes. Delivery learning may restart.
          </p>
          <div className="flex items-center gap-2">
            <Button className="h-9 bg-[#290BD9] hover:bg-[#2309C5]" onClick={handleConfirm} disabled={modeMutation.isPending}>
              {modeMutation.isPending ? 'Switching…' : `Switch to ${campaign.is_cbo ? 'ABO' : 'CBO'}`}
            </Button>
            <Button variant="outline" className="h-9 text-gray-900" onClick={() => setIsSwitching(false)} disabled={modeMutation.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MetaBudgetTab({
  campaign,
  currencySymbol,
}: {
  campaign: MetaCampaignDetailResponse;
  currencySymbol: string;
}) {
  return (
    <div className="space-y-6">
      {/* Underlined sub-tab chrome — the exact pattern Google's Budget tab uses (#5981). */}
      <div className="-mt-2.5 border-b border-gray-200">
        <div className="flex gap-0">
          <button
            type="button"
            className="px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px border-[#290BD9] text-gray-900"
          >
            Budget &amp; Bidding
          </button>
        </div>
      </div>

      {/* Budget Mode moved here from Overview (#6211) — the switcher's home per Jeff's review. */}
      <MetaBudgetModeSection campaign={campaign} currencySymbol={currencySymbol} />

      <div className="flex gap-6">
        <div className="w-1/2">
          <MetaDailyBudgetSection campaign={campaign} currencySymbol={currencySymbol} />
        </div>
        <div className="w-1/2">
          <MetaBiddingStrategyPanel campaign={campaign} currencySymbol={currencySymbol} />
        </div>
      </div>
    </div>
  );
}

// --- Conversions ----------------------------------------------------------------------------------

/**
 * Conversions — Google's tab layout with Meta-native semantics (#5981).
 *
 * One table for everything Meta counts as conversions, with a Source column mirroring Google's
 * (whose rows mix website/gtag and HubSpot conversion actions): "HubSpot · CAPI" rows are the
 * stages WE upload (linkable/unlinkable — the Meta analog of linking a conversion action) and
 * "Website · Pixel" rows are what Meta itself measured on the Pixel. The two counts are different
 * metrics — Pixel totals include CAPI deliveries (Meta dedups shared event ids) — so the caption
 * says so instead of pretending they line up. Delivery health lives on the Meta Performance page
 * (CAPI Health), not here.
 */
// The four attribution combinations, used to render a saved attribution_spec as a label (#6076).
// Display-only: Meta made attribution immutable after ad set creation (subcode 1504040). Meta's
// default for new ad sets is 7-day click + 1-day view.
const ATTRIBUTION_OPTIONS: { id: string; label: string; spec: MetaAttributionEntry[] }[] = [
  { id: '1c', label: '1-day click', spec: [{ event_type: 'CLICK_THROUGH', window_days: 1 }] },
  { id: '7c', label: '7-day click', spec: [{ event_type: 'CLICK_THROUGH', window_days: 7 }] },
  {
    id: '1c1v',
    label: '1-day click + 1-day view',
    spec: [
      { event_type: 'CLICK_THROUGH', window_days: 1 },
      { event_type: 'VIEW_THROUGH', window_days: 1 },
    ],
  },
  {
    id: '7c1v',
    label: '7-day click + 1-day view',
    spec: [
      { event_type: 'CLICK_THROUGH', window_days: 7 },
      { event_type: 'VIEW_THROUGH', window_days: 1 },
    ],
  },
];

const attributionOptionId = (spec: MetaAttributionEntry[] | null | undefined): string | null => {
  if (!spec) return null;
  const key = [...spec]
    .map((entry) => `${entry.event_type}:${entry.window_days}`)
    .sort()
    .join('|');
  return (
    ATTRIBUTION_OPTIONS.find(
      (option) =>
        option.spec
          .map((entry) => `${entry.event_type}:${entry.window_days}`)
          .sort()
          .join('|') === key,
    )?.id ?? null
  );
};

/**
 * Optimization: what the campaign's delivery optimizes toward (#6076). Conversion objectives
 * only — the parent hides it otherwise. The event is EDITABLE since #6212: Meta blocks in-place
 * edits on published ad sets (subcode 3260011), so a change RECREATES the ad set (same targeting,
 * budget, and ads; new promoted_object) — the confirmation says so before anything is sent.
 * Attribution stays display-only (creation-time-only on Meta, subcode 1504040), and instant-form
 * campaigns stay display-only too — they optimize toward the form, not a Pixel event.
 */
function MetaOptimizationSection({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  const { toast } = useToast();
  const defaultEventName = campaign.objective === 'OUTCOME_SALES' ? 'Purchase' : 'Lead';
  // An instant-form campaign (#6186) optimizes toward on-ad form submissions, not a Pixel event —
  // the ad set's LEAD_GENERATION goal is the marker (optimization_event is always null for it).
  const isInstantFormCampaign = campaign.ad_sets.some((adSet) => adSet.optimization_goal === 'LEAD_GENERATION');
  const outcome: MetaCampaignOutcome = campaign.objective === 'OUTCOME_SALES' ? 'WEBSITE_SALES' : 'WEBSITE_LEADS';
  const { data: eventOptions } = useQuery({
    ...metaConversionEventsQueryOptions(outcome),
    enabled: !isInstantFormCampaign,
  });
  const settingsMutation = useUpdateMetaConversionSettingsMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [pendingEvent, setPendingEvent] = useState<MetaConversionEventOption | null>(null);
  const canEdit = !isInstantFormCampaign && campaign.meta_campaign_id != null;

  const eventLabel = isInstantFormCampaign
    ? 'Instant form submissions (on-ad lead form)'
    : campaign.optimization_event
      ? `${campaign.optimization_event.name} (${campaign.optimization_event.type === 'standard' ? 'standard event' : 'custom conversion'})`
      : `${defaultEventName} (standard event, the objective's default)`;
  const attributionLabel = campaign.attribution_spec
    ? (ATTRIBUTION_OPTIONS.find((option) => option.id === attributionOptionId(campaign.attribution_spec))?.label ??
      'Custom')
    : "7-day click + 1-day view (Meta's default)";

  const optionKey = (option: MetaConversionEventOption) =>
    option.type === 'standard' ? `standard:${option.event}` : `custom:${option.custom_conversion_id}`;

  const handleConfirm = async () => {
    if (!pendingEvent) return;
    try {
      await settingsMutation.mutateAsync({
        slug: campaign.slug,
        body: {
          optimization_event: {
            type: pendingEvent.type,
            name: pendingEvent.name,
            event: pendingEvent.event ?? undefined,
            custom_conversion_id: pendingEvent.custom_conversion_id ?? undefined,
          },
        },
      });
      setPendingEvent(null);
      setIsEditing(false);
    } catch (error) {
      setPendingEvent(null);
      toast({
        title: 'Could Not Change Conversion Event',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-gray-900">Optimization</h3>
        <p className="text-sm text-gray-500">
          The conversion event Meta optimizes delivery toward, and how conversions are attributed.
        </p>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
          <span className="w-40 flex-shrink-0 text-xs font-medium uppercase tracking-wider text-gray-500">
            Optimization Event
          </span>
          {isEditing ? (
            <Select
              onValueChange={(key) => {
                const option = (eventOptions ?? []).find((candidate) => optionKey(candidate) === key);
                if (option) setPendingEvent(option);
              }}
            >
              <SelectTrigger className="h-9 w-72">
                <SelectValue placeholder={eventLabel} />
              </SelectTrigger>
              <SelectContent>
                {(eventOptions ?? []).map((option) => (
                  <SelectItem key={optionKey(option)} value={optionKey(option)}>
                    {option.name} {option.type === 'custom' ? '(custom conversion)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm text-gray-900">{eventLabel}</span>
          )}
          {canEdit && (
            <Button
              variant="outline"
              className="ml-auto h-9 text-gray-900"
              onClick={() => setIsEditing((prev) => !prev)}
              disabled={settingsMutation.isPending}
            >
              {isEditing ? 'Cancel' : 'Change'}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="w-40 flex-shrink-0 text-xs font-medium uppercase tracking-wider text-gray-500">
            Attribution
          </span>
          <span className="text-sm text-gray-900">{attributionLabel}</span>
        </div>
      </div>
      <p className="mt-2 text-sm italic text-gray-500">
        {isInstantFormCampaign
          ? 'Instant-form campaigns optimize toward form submissions: there is no conversion event to switch.'
          : 'Changing the conversion event recreates the ad set with the same targeting, budget, and ads. Attribution is fixed at creation.'}
      </p>
      <ConformationModal
        isOpen={pendingEvent != null}
        onClose={() => setPendingEvent(null)}
        onConfirm={handleConfirm}
        title="Change Conversion Event"
        description={`Switch delivery optimization to "${pendingEvent?.name ?? ''}"? The ad set is recreated with the same targeting, budget, and ads, and delivery learning starts over.`}
        confirmText="Change Event"
        cancelText="Cancel"
        isLoading={settingsMutation.isPending}
      />
    </div>
  );
}

export function MetaConversionsTab({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  const { toast } = useToast();
  const { data: metaStatus } = useQuery(metaIntegrationStatusQueryOptions());
  const { data: conversions, isLoading: isLoadingConversions } = useQuery(metaConversionsQueryOptions());
  const linkMutation = useLinkMetaConversionMutation();
  const unlinkMutation = useUnlinkMetaConversionMutation();
  const selected = metaStatus?.selected_ad_account;

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedStageIds, setSelectedStageIds] = useState<Set<number>>(new Set());
  const [unlinkModal, setUnlinkModal] = useState<{ isOpen: boolean; stageMappingId: number; name: string }>({
    isOpen: false,
    stageMappingId: 0,
    name: '',
  });

  const available = conversions?.available ?? [];
  const rows: ConversionsTableRow[] = [
    ...(conversions?.linked ?? [])
      .filter((row) => row.is_active)
      .map((row) => ({
        key: `hubspot-${row.stage_mapping_id}`,
        name: row.name,
        event_name: row.event_name,
        source: 'HubSpot · CAPI',
        count: row.uploads_last_30d,
        stage_mapping_id: row.stage_mapping_id,
      })),
    ...(conversions?.pixel_events ?? []).map((row) => ({
      key: `pixel-${row.event_name}`,
      name: row.event_name,
      event_name: row.event_name,
      source: 'Website · Pixel',
      count: row.count_last_7d,
    })),
  ];

  const mutationErrorToast = (error: unknown) =>
    toast({
      title: 'Could Not Update Conversions',
      description: error instanceof Error ? error.message : 'Please try again.',
      variant: 'destructive',
    });

  const handleLinkSelected = async () => {
    const linked: number[] = [];
    try {
      for (const stageMappingId of selectedStageIds) {
        await linkMutation.mutateAsync(stageMappingId);
        linked.push(stageMappingId);
      }
      setShowAddDialog(false);
      setSelectedStageIds(new Set());
    } catch (error) {
      // Mid-loop failure (#6163 P1-9): drop the stages that DID link so a retry doesn't re-post
      // already-linked ids; leave the rest selected for the retry.
      setSelectedStageIds((prev) => {
        const next = new Set(prev);
        linked.forEach((id) => next.delete(id));
        return next;
      });
      mutationErrorToast(error);
    }
  };

  const columns: ColumnDef<ConversionsTableRow, unknown>[] = [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'source', header: 'Source' },
    { accessorKey: 'event_name', header: 'Meta Event' },
    { accessorKey: 'count', header: 'Conversions' },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        // Only HubSpot rows unlink — Pixel rows are events wired on the client's website.
        row.original.stage_mapping_id != null ? (
          <IconButton
            variant="destructive"
            aria-label="Unlink conversion"
            onClick={() =>
              setUnlinkModal({ isOpen: true, stageMappingId: row.original.stage_mapping_id!, name: row.original.name })
            }
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Conversions</h3>
            {/* Mirrors Google's "Link conversion actions to track performance for this campaign."
                — same sentence shape, Meta's honest scope (#5981, no text drift). */}
            <p className="text-sm text-gray-500">Link conversion stages to track performance for your campaigns.</p>
          </div>
          <Button
            variant="outline"
            className="h-9 text-gray-900"
            onClick={() => setShowAddDialog(true)}
            disabled={isLoadingConversions}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Conversion
          </Button>
        </div>
        {isLoadingConversions ? (
          <p className="py-2 text-sm text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Plus className="h-12 w-12 text-gray-400" />}
            title="No Conversions Linked"
            description="Link conversion stages to this account to track ROAS"
          />
        ) : (
          <>
            <DataTable data={rows} columns={columns} variant="flush" pageSize={10} getRowId={(row) => row.key} />
            <p className="mt-2 text-xs text-gray-500">
              HubSpot counts are conversions uploaded in the last 30 days. Website counts are events Meta
              measured on the Pixel in the last 7 days and include Conversions API deliveries.
            </p>
          </>
        )}
      </div>

      {/* Conversion objectives only — for traffic/awareness/engagement there is no Pixel event
          to optimize toward (the backend 400s the same way). */}
      {(campaign.objective === 'OUTCOME_LEADS' || campaign.objective === 'OUTCOME_SALES') && (
        <MetaOptimizationSection campaign={campaign} />
      )}

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Meta Pixel</h3>
        <p className="text-sm text-gray-500 mb-3">
          Conversions are attributed through the ad account&apos;s Pixel.
        </p>
        {selected?.pixel_id ? (
          <div className="rounded-lg border p-4">
            <p className="text-sm font-medium text-gray-900">{selected.pixel_name ?? 'Meta Pixel'}</p>
            <p className="text-xs text-gray-500 mt-0.5 font-mono">{selected.pixel_id}</p>
          </div>
        ) : (
          <div className="border-l-4 border-[#FDE68A] bg-[#FEF3C7] text-[#A16207] p-3 text-sm rounded-r-lg">
            <span className="inline-flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              No Pixel selected for this ad account. Pick one in Settings → Integrations.
            </span>
          </div>
        )}
      </div>

      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) setSelectedStageIds(new Set()); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Conversion</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            Pick the HubSpot conversion stages to upload to Meta. Each is sent as a standard Lead event
            through the account&apos;s Pixel.
          </p>
          {available.length === 0 ? (
            <p className="py-2 text-sm text-gray-500">
              All HubSpot conversion stages are already linked. Manage stages in Settings → Integrations.
            </p>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto py-1">
              {available.map((stage) => (
                <label key={stage.stage_mapping_id} className="flex cursor-pointer items-center gap-3 rounded-md border p-3">
                  <Checkbox
                    checked={selectedStageIds.has(stage.stage_mapping_id)}
                    onCheckedChange={(checked) =>
                      setSelectedStageIds((prev) => {
                        const next = new Set(prev);
                        if (checked === true) next.add(stage.stage_mapping_id);
                        else next.delete(stage.stage_mapping_id);
                        return next;
                      })
                    }
                  />
                  <span className="text-sm text-gray-900">{stage.name}</span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="h-9 text-gray-500" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              className="h-9 text-gray-900"
              onClick={handleLinkSelected}
              disabled={linkMutation.isPending || selectedStageIds.size === 0}
            >
              {linkMutation.isPending && <Spinner size="sm" className="mr-2" />}
              Link Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={unlinkModal.isOpen}
        onClose={() => setUnlinkModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={async () => {
          try {
            await unlinkMutation.mutateAsync(unlinkModal.stageMappingId);
          } catch (error) {
            mutationErrorToast(error);
          } finally {
            setUnlinkModal((prev) => ({ ...prev, isOpen: false }));
          }
        }}
        title="Unlink Conversion"
        confirmText="Unlink"
        description={`Stop uploading "${unlinkModal.name}" conversions to Meta? Upload history is kept, and you can re-link it any time.`}
      />
    </div>
  );
}

